import { useMemo } from 'react';
import { CallPathV1 } from '@frontend/api-call-path';
import { PhoneNumbersV1 } from '@frontend/api-phone-numbers';
import { PhoneTreeV1 } from '@frontend/api-phone-trees';
import {
  useAnalyticsOrgLocations,
  formatEndDateWithTimeZone,
  formatStartDateWithTimeZone,
} from '@frontend/analytics-shared';
import { AggregatedPhoneTreeCallVolumeType } from '../components/charts/shared-types';
import { useGetChartPayloadAndConfig } from './use-get-chart-payload-and-config';
import { usePhoneAnalyticsShallowStore } from './use-phone-analytics-store';

export const useFetchAggregatedPhoneTreeCallVolume = ({
  filters,
  isDemoAccount,
  previousGlsLocationIds,
  demoData,
}: {
  filters: ReturnType<typeof usePhoneAnalyticsShallowStore>['filters'];
  isDemoAccount: boolean | undefined;
  previousGlsLocationIds: string | undefined;
  demoData?: ReturnType<typeof usePhoneAnalyticsShallowStore>['demoData'];
}) => {
  const { locationNames, accessibleLocationMap } = useAnalyticsOrgLocations({
    isAllAccessScopeEnabled: true,
    isDemoAccount,
    module: 'PHONE',
  });

  const tenantIds = Array.from(
    new Set(
      filters.location_id
        ?.map((loc) => accessibleLocationMap?.[loc]?.phoneTenantId)
        .filter((id): id is string => typeof id === 'string')
    )
  );

  const { data: phoneTrees } = PhoneTreeV1.Queries.useListByTenantsQuery(
    {
      tenantIds,
    },
    {
      enabled: tenantIds.length > 0 && !isDemoAccount,
    }
  );

  const { applicableFilters, config } = useGetChartPayloadAndConfig({
    filters,
    isDemoAccount,
    previousGlsLocationIds,
    uniqueId: 'routing-analytics',
  });

  const startTime = formatStartDateWithTimeZone(applicableFilters.start_date);
  const endTime = formatEndDateWithTimeZone(applicableFilters.end_date);

  const tenantPhoneTreeIdsMap = tenantIds.reduce((acc, tenantId) => {
    const phoneTreeIds = phoneTrees?.phoneTrees?.[tenantId]?.phoneTrees?.map((pt) => pt.id) ?? [];
    if (phoneTreeIds.length > 0) {
      acc[tenantId] = { phoneTreeIds };
    }
    return acc;
  }, {} as Record<string, { phoneTreeIds: string[] }>);

  // Disable query if end date is before September 1, 2025
  const minDate = new Date('2025-09-01');
  const startDate = applicableFilters.start_date ? new Date(applicableFilters.start_date) : undefined;
  const isDateValid = startDate ? startDate >= minDate : false;

  const { data, isLoading: isAnalyticsLoading } =
    CallPathV1.CallPathQueries.useFetchPhoneTreeAnalyticsForMultiTenantQuery(
      {
        startTime,
        endTime,
        tenantPhoneTreeIdsMap,
      },
      {
        enabled: config.enabled && Object.keys(tenantPhoneTreeIdsMap).length > 0 && isDateValid,
      }
    );

  const { data: allPhoneNumbers } = PhoneNumbersV1.Queries.useListPhoneNumbersForLocationsQuery({
    request: { locationIds: filters.location_id ?? [], voice: true },
    options: {
      enabled: !!filters.location_id?.length && filters.location_id?.length > 0,
      select: ({ phoneNumbers }) => phoneNumbers ?? [],
    },
  });

  const aggregatedData: AggregatedPhoneTreeCallVolumeType[] = useMemo(() => {
    if (!data) return [];
    const mappedIds = data?.phoneTreeAnalytics.map((item) => item.phoneTreeId);
    const additionalPhoneTrees: { phoneTreeName: string; phoneTreeId: string }[] = [];

    for (const tenantId of tenantIds) {
      const filteredPhoneTrees = phoneTrees?.phoneTrees?.[tenantId]?.phoneTrees?.filter(
        (pt) => !mappedIds?.includes(pt.id)
      );

      if (filteredPhoneTrees) {
        filteredPhoneTrees.forEach((pt2) => {
          additionalPhoneTrees.push({
            phoneTreeName: pt2.name,
            phoneTreeId: pt2.id,
          });
        });
      }
    }

    return [...data.phoneTreeAnalytics, ...additionalPhoneTrees];
  }, [data, phoneTrees?.phoneTrees, tenantIds]);

  const dataToShow =
    isDemoAccount && demoData
      ? (demoData?.chartsData.phoneTreesCallVolume.data as AggregatedPhoneTreeCallVolumeType[])
      : aggregatedData;

  const phoneNumbersToShow =
    isDemoAccount && demoData
      ? (demoData?.chartsData.phoneTreesCallVolume.phoneNumbers as unknown as typeof allPhoneNumbers)
      : allPhoneNumbers;

  const locationNamesToShow =
    isDemoAccount && demoData ? demoData?.chartsData.phoneTreesCallVolume.locationNames : locationNames;

  return {
    phoneTrees,
    data: dataToShow,
    isLoading: isAnalyticsLoading,
    allPhoneNumbers: phoneNumbersToShow,
    locationNames: locationNamesToShow,
    isDateValid,
  };
};
